import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/shared/hooks/useAuth';

/** Routes that remain accessible even when subscription is blocked */
const SUBSCRIPTION_EXEMPT_PATHS = ['/billing', '/settings', '/change-password', '/sessions'];

function isSubscriptionExempt(pathname: string): boolean {
  return SUBSCRIPTION_EXEMPT_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + '/'),
  );
}

export interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
  requiredPermissions?: string[];
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermissions,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasRole, hasPermission, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ returnTo: location.pathname + location.search }}
        replace
      />
    );
  }

  // Check subscription status — block expired/cancelled users
  const subStatus = user?.subscriptionStatus;
  if (
    (subStatus === 'expired' || subStatus === 'cancelled') &&
    !isSubscriptionExempt(location.pathname)
  ) {
    return <Navigate to="/subscription-blocked" replace />;
  }

  // Loja desativada pela Vapt Menu: tela própria, porque a de assinatura
  // manda assinar um plano, e pagar não reativa a loja.
  if (subStatus === 'deactivated' && !isSubscriptionExempt(location.pathname)) {
    return <Navigate to="/store-deactivated" replace />;
  }

  // Check role
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/403" replace />;
  }

  // Check permissions (all required)
  if (requiredPermissions?.length) {
    const hasAll = requiredPermissions.every((p) => hasPermission(p));
    if (!hasAll) {
      return <Navigate to="/403" replace />;
    }
  }

  return <>{children}</>;
}
